import { useState, useEffect } from 'react';
import adminApi from '@/lib/adminApi';
import { toast } from 'sonner';
import { Megaphone, Save, Loader2, Eye, EyeOff, Image, ExternalLink, Clock, Repeat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';


export const PromoBannerTab = ({ onRefresh }) => {
  const [banner, setBanner] = useState({
    image_url: '',
    mobile_image_url: '',
    link_url: '',
    active: false,
    show_always: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBanner();
  }, []);

  const fetchBanner = async () => {
    try {
      const res = await adminApi.get(`/promo-banner`);
      setBanner(res.data);
    } catch (error) {
      console.error('Error fetching promo banner:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/promo-banner`, banner, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      toast.success('Banner promocional guardado');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Error al guardar el banner');
      console.error('Error saving promo banner:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone className="text-[#E91E8C]" size={24} />
          <h2 className="text-xl font-bold text-black">Banner Promocional (Sticky)</h2>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#E91E8C] hover:bg-[#E91E8C]/80 text-white"
        >
          {saving ? (
            <>
              <Loader2 className="animate-spin mr-2" size={16} />
              Guardando...
            </>
          ) : (
            <>
              <Save className="mr-2" size={16} />
              Guardar
            </>
          )}
        </Button>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-700">
          Este banner aparece en la parte inferior del sitio después de 2 segundos. 
          <br /><br />
          <strong>Tip:</strong> Usa una imagen horizontal con tus personajes y un llamado a la acción claro.
          Tamaño recomendado: 1200x200 px (desktop) y 600x150 px (móvil).
        </p>
      </div>

      {/* Settings Form */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
        {/* Active Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            {banner.active ? (
              <Eye className="text-green-500" size={20} />
            ) : (
              <EyeOff className="text-gray-400" size={20} />
            )}
            <div>
              <p className="font-medium text-gray-900">Banner Activo</p>
              <p className="text-sm text-gray-500">
                {banner.active ? 'El banner está visible en el sitio' : 'El banner está oculto'}
              </p>
            </div>
          </div>
          <Switch
            checked={banner.active}
            onCheckedChange={(checked) => setBanner({ ...banner, active: checked })}
          />
        </div>

        {/* Show Always Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            {banner.show_always ? (
              <Repeat className="text-[#E91E8C]" size={20} />
            ) : (
              <Clock className="text-gray-400" size={20} />
            )}
            <div>
              <p className="font-medium text-gray-900">Frecuencia de aparición</p>
              <p className="text-sm text-gray-500">
                {banner.show_always 
                  ? 'Aparece siempre (cada vez que se carga la página)' 
                  : 'Aparece una vez cada 24 horas (después de cerrar)'}
              </p>
            </div>
          </div>
          <Switch
            checked={banner.show_always}
            onCheckedChange={(checked) => setBanner({ ...banner, show_always: checked })}
          />
        </div>

        {/* Desktop Image */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Image size={16} />
            Imagen (Desktop)
            <span className="text-xs text-gray-400 font-normal ml-2">— Recomendado: 1200x200 px</span>
          </label>
          <Input
            type="url"
            value={banner.image_url}
            onChange={(e) => setBanner({ ...banner, image_url: e.target.value })}
            placeholder="https://ejemplo.com/banner-desktop.jpg"
            className="bg-gray-50 border-gray-200"
          />
          {banner.image_url && (
            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600 block mb-2">Vista previa (Desktop):</span>
              <img 
                src={banner.image_url} 
                alt="Banner desktop preview" 
                className="max-w-full h-auto max-h-32 rounded border border-gray-200"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
        </div>

        {/* Mobile Image */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Image size={16} />
            Imagen (Móvil) - Opcional
            <span className="text-xs text-gray-400 font-normal ml-2">— Recomendado: 600x150 px</span>
          </label>
          <Input
            type="url"
            value={banner.mobile_image_url}
            onChange={(e) => setBanner({ ...banner, mobile_image_url: e.target.value })}
            placeholder="https://ejemplo.com/banner-movil.jpg"
            className="bg-gray-50 border-gray-200"
          />
          <p className="text-xs text-gray-500">
            Si no se proporciona, se usará la imagen de desktop
          </p>
          {banner.mobile_image_url && (
            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600 block mb-2">Vista previa (Móvil):</span>
              <img 
                src={banner.mobile_image_url} 
                alt="Banner mobile preview" 
                className="max-w-xs h-auto max-h-24 rounded border border-gray-200"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
        </div>

        {/* Link URL */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <ExternalLink size={16} />
            URL de Enlace
          </label>
          <Input
            type="url"
            value={banner.link_url}
            onChange={(e) => setBanner({ ...banner, link_url: e.target.value })}
            placeholder="https://whatsapp.com/channel/tu-canal"
            className="bg-gray-50 border-gray-200"
          />
          <p className="text-xs text-gray-500">
            Al hacer clic en el banner, se abrirá este enlace en una nueva pestaña
          </p>
        </div>
      </div>
    </div>
  );
};
